import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export const DEFAULT_STATUS_TIME_KEYS = [
  'timestamp',
  'createdDate',
  'createdAt',
  'created_at',
  'created_date',
  'updatedAt',
  'updated_at',
  'time',
  'date'
] as const;

type PrimitiveDateInput = Date | string | number | null | undefined;

function getTimeZoneSuffixPattern(): RegExp {
  return /([zZ]|[+-]\d{2}:?\d{2})$/;
}

function toNumeric(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function resolveUpdateId(entry: any): number {
  return toNumeric(
    entry?.incidentUpdateId ??
      entry?.incident_update_id ??
      entry?.updateId ??
      entry?.update_id ??
      entry?.id
  );
}

export function extractTimestampValue(source: any, additionalKeys: string[] = []): unknown {
  if (!source || typeof source !== 'object') {
    return source;
  }

  const keys = [...DEFAULT_STATUS_TIME_KEYS, ...additionalKeys];
  for (const key of keys) {
    const value = source?.[key];
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return value;
    }
  }

  return undefined;
}

export function parseApiDateAsUtc(value: PrimitiveDateInput): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value);
  }

  if (typeof value === 'number') {
    const dateFromNumber = new Date(value);
    return Number.isNaN(dateFromNumber.getTime()) ? null : dateFromNumber;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  if (!normalized) {
    return null;
  }

  const hasExplicitZone = getTimeZoneSuffixPattern().test(normalized);
  const parsed = hasExplicitZone ? dayjs(normalized) : dayjs.utc(normalized);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.toDate();
}

export function parseTimestampFromSource(source: any, additionalKeys: string[] = []): Date | null {
  const rawValue = extractTimestampValue(source, additionalKeys);
  return parseApiDateAsUtc(rawValue as PrimitiveDateInput);
}

export function hasValidTimestamp(value: PrimitiveDateInput): boolean {
  return !!parseApiDateAsUtc(value);
}

export function isValidIanaTimeZone(value?: string | null): value is string {
  if (!value) {
    return false;
  }
  try {
    dayjs().tz(value);
    return true;
  } catch {
    return false;
  }
}

export function getTimeZoneAbbreviation(timeZone?: string | null): string {
  if (!isValidIanaTimeZone(timeZone)) {
    return '';
  }
  return dayjs().tz(timeZone).format('z');
}

export function formatDateInTimeZone(
  value: PrimitiveDateInput,
  timeZone?: string | null,
  format: string = 'MMM d, y h:mm a'
): string {
  const date = parseApiDateAsUtc(value);
  if (!date) {
    return '';
  }

  const formattedDate = isValidIanaTimeZone(timeZone)
    ? dayjs(date).tz(timeZone)
    : dayjs.utc(date);

  if (format === 'MMM d, y') return formattedDate.format('MMM D, YYYY');
  if (format === 'shortTime') return formattedDate.format('h:mm A');
  if (format === 'MMM d, y h:mm a') return formattedDate.format('MMM D, YYYY h:mm A');
  if (format === 'MMM d, y, h:mm a') return formattedDate.format('MMM D, YYYY, h:mm A');
  return formattedDate.format(format);
}

export function compareTimelineEntriesByTimestampDesc(
  a: any,
  b: any,
  timestampResolver: (entry: any) => PrimitiveDateInput = (entry) => extractTimestampValue(entry) as PrimitiveDateInput,
  idResolver: (entry: any) => unknown = resolveUpdateId
): number {
  const aTime = parseApiDateAsUtc(timestampResolver(a))?.getTime() || 0;
  const bTime = parseApiDateAsUtc(timestampResolver(b))?.getTime() || 0;

  if (aTime === bTime) {
    return toNumeric(idResolver(b)) - toNumeric(idResolver(a));
  }

  return bTime - aTime;
}

export function formatRelativeTimeLabel(
  value: PrimitiveDateInput,
  nowDate: Date = new Date(),
  timeZone?: string | null
): string {
  const date = parseApiDateAsUtc(value);
  if (!date) {
    return 'Time unavailable';
  }

  const hasTimeZone = !!timeZone;
  let nowTime = nowDate.getTime();
  let targetTime = date.getTime();

  if (hasTimeZone) {
    try {
      const zone = String(timeZone);
      nowTime = dayjs(nowDate).tz(zone).valueOf();
      targetTime = dayjs(date).tz(zone).valueOf();
    } catch {
      nowTime = nowDate.getTime();
      targetTime = date.getTime();
    }
  }

  const diffMs = nowTime - targetTime;
  if (diffMs <= 0) {
    return 'now';
  }

  const diffSeconds = Math.floor(diffMs / 1000);
  if (diffSeconds < 5) {
    return 'now';
  }
  if (diffSeconds < 60) {
    return `${diffSeconds} sec${diffSeconds === 1 ? '' : 's'} ago`;
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return 'less than an hour ago';
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  }

  const diffYears = Math.max(1, Math.floor(diffDays / 365));
  return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
}
